import React, { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, List, ListItem, ListItemText,
  ListItemAvatar, Avatar, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Alert
} from '@mui/material';
import {
  Work as WorkIcon, Check as CheckIcon, Close as CloseIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import apiClient from '../../request/http-common';

const JobApprovalAdmin = () => {
  const queryClient = useQueryClient();
  const [selectedJob, setSelectedJob] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');

  const { data: pendingJobs, isLoading, error } = useQuery(
    'adminPendingJobs',
    async () => {
      const response = await apiClient.get('/api/admin/jobs/pending');
      return response.data;
    },
    {
      refetchInterval: 30000,
      onError: (error) => {
        console.error('Admin Job Approvals API Error:', error);
      }
    }
  );

  const adminApprovalMutation = useMutation(
    async ({ jobId, action, adminNotes }) => {
      const response = await apiClient.post(`/api/admin/jobs/${jobId}/approve`, {
        action,
        admin_notes: adminNotes
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminPendingJobs');
        setDialogOpen(false);
        setSelectedJob(null);
        setAdminNotes('');
      },
      onError: (error) => {
        console.error('Admin approval failed:', error);
        alert('Failed to process admin approval: ' + (error.response?.data?.message || error.message));
      }
    }
  );

  const handleAdminAction = (action) => {
    if (selectedJob) {
      adminApprovalMutation.mutate({
        jobId: selectedJob.id,
        action,
        adminNotes
      });
    }
  };

  const openDialog = (job) => {
    setSelectedJob(job);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Loading pending jobs...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load pending jobs: {error.message}
        </Alert>
      </Box>
    );
  }

  const jobs = pendingJobs?.jobs || [];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" sx={{
        color: '#FFFFFF',
        fontWeight: 700,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <WorkIcon sx={{ color: '#F7931A' }} />
        Job Posting Approval Dashboard
      </Typography>

      <Typography variant="body1" sx={{ color: '#6C7293', mb: 3 }}>
        Review and approve or reject job postings submitted by employers.
      </Typography>

      {jobs.length === 0 ? (
        <Card sx={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
          border: '1px solid #2B3139'
        }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <WorkIcon sx={{ fontSize: 64, color: '#6C7293', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#FFFFFF', mb: 1 }}>
              No Pending Job Postings
            </Typography>
            <Typography variant="body2" sx={{ color: '#6C7293' }}>
              All job postings have been processed.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <List>
          {jobs.map((job) => (
            <ListItem
              key={job.id}
              sx={{
                border: '1px solid #2B3139',
                borderRadius: 1,
                mb: 2,
                background: 'linear-gradient(135deg, #161A1E 0%, #1E2328 100%)',
                '&:hover': {
                  borderColor: '#F7931A'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar sx={{ bgcolor: '#F7931A' }}>
                  <WorkIcon />
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ color: '#FFFFFF' }}>
                      {job.job_title}
                    </Typography>
                    <Chip
                      label={job.company_name}
                      size="small"
                      sx={{ background: 'rgba(247, 147, 26, 0.2)', color: '#F7931A' }}
                    />
                    <Chip
                      label="Pending"
                      color="warning"
                      size="small"
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Posted by:</strong> {job.employer?.name || job.employer?.email}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Location:</strong> {job.location || 'Not specified'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Type:</strong> {job.job_type} | <strong>Level:</strong> {job.experience_level}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Salary:</strong> {job.salary_range || 'Not specified'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Description:</strong> {job.job_description?.substring(0, 100)}...
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#B7BDC6', mb: 0.5 }}>
                      <strong>Posted:</strong> {new Date(job.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                }
              />

              <Button
                variant="contained"
                onClick={() => openDialog(job)}
                sx={{
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                  }
                }}
              >
                Review
              </Button>
            </ListItem>
          ))}
        </List>
      )}

      {/* Admin Action Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#FFFFFF', background: '#1a1a1a' }}>
          Review Job Posting
        </DialogTitle>
        <DialogContent sx={{ background: '#1a1a1a', color: '#FFFFFF' }}>
          {selectedJob && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: '#F7931A' }}>
                {selectedJob.job_title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Company:</strong> {selectedJob.company_name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Posted by:</strong> {selectedJob.employer?.name || selectedJob.employer?.email}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Location:</strong> {selectedJob.location || 'Not specified'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Type:</strong> {selectedJob.job_type} | <strong>Level:</strong> {selectedJob.experience_level}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Salary Range:</strong> {selectedJob.salary_range || 'Not specified'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Application Deadline:</strong> {selectedJob.application_deadline ? new Date(selectedJob.application_deadline).toLocaleDateString() : 'Not specified'}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Description:</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#B7BDC6', whiteSpace: 'pre-wrap' }}>
                {selectedJob.job_description}
              </Typography>
              {selectedJob.requirements && (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>Requirements:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: '#B7BDC6', whiteSpace: 'pre-wrap' }}>
                    {selectedJob.requirements}
                  </Typography>
                </>
              )}
              <Typography variant="body2" sx={{ mb: 2, color: '#6C7293' }}>
                <strong>Posted:</strong> {new Date(selectedJob.created_at).toLocaleString()}
              </Typography>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Admin Notes (Optional)"
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                sx={{
                  mt: 2,
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#6C7293' },
                    '&:hover fieldset': { borderColor: '#F7931A' },
                  },
                  '& .MuiInputLabel-root': { color: '#6C7293' },
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ background: '#1a1a1a', p: 2, gap: 1 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ color: '#6C7293' }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleAdminAction('rejected')}
            variant="outlined"
            color="error"
            disabled={adminApprovalMutation.isLoading}
            sx={{
              borderColor: '#F84960',
              color: '#F84960',
              '&:hover': {
                borderColor: '#DC2626',
                backgroundColor: 'rgba(248, 73, 96, 0.1)'
              }
            }}
          >
            Reject
          </Button>
          <Button
            onClick={() => handleAdminAction('approved')}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)'
              }
            }}
            disabled={adminApprovalMutation.isLoading}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobApprovalAdmin;

